import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  LayoutTemplate, MoreVertical, Trash2, Pencil, FileText, Receipt, Loader2, Search, Copy, PlusCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useListCustomers, useCreateCustomer, getListCustomersQueryKey } from "@workspace/api-client-react";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { CreateCustomerModal, type CreateCustomerFormValues } from "@/components/create-customer-modal";
import { ProTeaserSection } from "@/components/pro-gate";
import { cn } from "@/lib/utils";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Template {
  id: number;
  name: string;
  description: string | null;
  type: string;
  lineItemCount: number;
  createdAt: string;
  updatedAt: string;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function TemplatesPage() {
  usePageTitle("Templates");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Template | null>(null);
  const [renameName, setRenameName] = useState("");
  const [renameDesc, setRenameDesc] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isDuplicating, setIsDuplicating] = useState(false);

  const [useOpen, setUseOpen] = useState(false);
  const [useTarget, setUseTarget] = useState<Template | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState("");

  const queryClient = useQueryClient();
  const { data: customers } = useListCustomers();
  const createCustomer = useCreateCustomer();

  async function loadTemplates() {
    setIsLoading(true);
    try {
      const res = await fetch(`${BASE}/api/templates`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setTemplates(data);
    } catch {
      toast({ title: "Failed to load templates", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (isPro) loadTemplates();
    else setIsLoading(false);
  }, [isPro]);

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.description || "").toLowerCase().includes(search.toLowerCase())
  );

  async function handleRename() {
    if (!renameTarget || !renameName.trim()) return;
    setIsRenaming(true);
    try {
      const res = await fetch(`${BASE}/api/templates/${renameTarget.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: renameName.trim(), description: renameDesc.trim() || null }),
      });
      if (!res.ok) throw new Error("Failed");
      setTemplates(prev => prev.map(t => t.id === renameTarget.id ? { ...t, name: renameName.trim(), description: renameDesc.trim() || null } : t));
      setRenameOpen(false);
      toast({ title: "Template updated" });
    } catch {
      toast({ title: "Failed to update template", variant: "destructive" });
    } finally {
      setIsRenaming(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${BASE}/api/templates/${deleteTarget.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      setTemplates(prev => prev.filter(t => t.id !== deleteTarget.id));
      setDeleteOpen(false);
      toast({ title: "Template deleted" });
    } catch {
      toast({ title: "Failed to delete template", variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleDuplicate(template: Template) {
    setIsDuplicating(true);
    try {
      const res = await fetch(`${BASE}/api/templates/${template.id}/duplicate`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setTemplates(prev => [data, ...prev]);
      toast({
        title: "Template duplicated",
        description: "Use Rename to give it a new name.",
      });
    } catch {
      toast({ title: "Failed to duplicate template", variant: "destructive" });
    } finally {
      setIsDuplicating(false);
    }
  }

  async function handleUse() {
    if (!useTarget || !selectedCustomerId) return;
    setIsCreating(true);
    try {
      const endpoint = useTarget.type === "quote" ? "create-estimate" : "create-invoice";
      const res = await fetch(`${BASE}/api/templates/${useTarget.id}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ customerId: selectedCustomerId }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setUseOpen(false);
      toast({ title: `${useTarget.type === "quote" ? "Quote" : "Invoice"} created` });
      navigate(useTarget.type === "quote" ? `/quotes/${data.id}` : `/invoices/${data.id}`);
    } catch {
      toast({ title: "Failed to create document", variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  }

  function handleCreateCustomer(data: CreateCustomerFormValues) {
    createCustomer.mutate(
      {
        data: {
          name: data.name,
          email: data.email || undefined,
          phone: data.phone || undefined,
          addressLine1: data.addressLine1 || undefined,
        },
      },
      {
        onSuccess: (newCustomer) => {
          queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
          setSelectedCustomerId(newCustomer.id);
          setSelectedCustomerName(newCustomer.name);
          setCustomerSearch("");
          setCreateModalOpen(false);
        },
        onError: () => {
          toast({ title: "Failed to create customer", variant: "destructive" });
        },
      }
    );
  }

  const filteredCustomers = (customers || []).filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <LayoutTemplate className="w-8 h-8 text-primary" />
            Templates
          </h1>
          <p className="text-muted-foreground mt-1">Reusable quote and invoice templates to speed up your workflow.</p>
        </div>
      </div>

      {!isPro ? (
        <ProTeaserSection
          title="Templates"
          description="Save any quote or invoice as a reusable template — start new jobs in seconds instead of from scratch."
        >
          <div className="grid gap-3">
            {[
              { type: "quote", name: "Standard Quote", desc: "3 line items" },
              { type: "invoice", name: "Project Invoice", desc: "5 line items" },
              { type: "quote", name: "Service Agreement", desc: "2 line items" },
            ].map((item, i) => (
              <Card key={i} className="border-border/50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="mt-0.5 flex-shrink-0 p-2 rounded-lg bg-muted">
                        {item.type === "quote" ? (
                          <FileText className="h-4 w-4 text-blue-600" />
                        ) : (
                          <Receipt className="h-4 w-4 text-violet-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground">{item.name}</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs px-2 py-0 capitalize",
                              item.type === "quote"
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-violet-50 text-violet-700 border-violet-200"
                            )}
                          >
                            {item.type === "quote" ? "Quote" : "Invoice"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                          <span>{item.desc}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="default" disabled>Use</Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ProTeaserSection>
      ) : (<>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates…"
          className="pl-10 bg-card"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <LayoutTemplate className="h-12 w-12 text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-1">
            {search ? "No templates match your search" : "No templates yet"}
          </h3>
          <p className="text-muted-foreground text-sm max-w-xs">
            {search ? "Try a different search term." : "Open any quote or invoice, click the \u22EE menu, and choose \"Save as Template\"."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(template => (
            <Card key={template.id} className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="mt-0.5 flex-shrink-0 p-2 rounded-lg bg-muted">
                      {template.type === "quote" ? (
                        <FileText className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Receipt className="h-4 w-4 text-violet-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground truncate">{template.name}</h3>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs px-2 py-0 capitalize",
                            template.type === "quote"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-violet-50 text-violet-700 border-violet-200"
                          )}
                        >
                          {template.type === "quote" ? "Quote" : "Invoice"}
                        </Badge>
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">{template.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        <span>{template.lineItemCount} line item{template.lineItemCount !== 1 ? "s" : ""}</span>
                        <span>·</span>
                        <span>Updated {formatDate(template.updatedAt)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      onClick={() => {
                        setUseTarget(template);
                        setSelectedCustomerId(null);
                        setSelectedCustomerName("");
                        setCustomerSearch("");
                        setUseOpen(true);
                      }}
                    >
                      Use
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setRenameTarget(template);
                            setRenameName(template.name);
                            setRenameDesc(template.description || "");
                            setRenameOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={isDuplicating}
                          onClick={() => handleDuplicate(template)}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive focus:bg-destructive/10"
                          onClick={() => {
                            setDeleteTarget(template);
                            setDeleteOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={o => { if (!isRenaming) setRenameOpen(o); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Template</DialogTitle>
            <DialogDescription>Update the name and description of this template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Template Name</label>
              <Input
                value={renameName}
                onChange={e => setRenameName(e.target.value)}
                placeholder="Template name"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input
                value={renameDesc}
                onChange={e => setRenameDesc(e.target.value)}
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isRenaming} onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button disabled={!renameName.trim() || isRenaming} onClick={handleRename}>
              {isRenaming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={o => { if (!isDeleting) setDeleteOpen(o); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={e => { e.preventDefault(); handleDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Use dialog */}
      <Dialog open={useOpen} onOpenChange={o => { if (!isCreating) setUseOpen(o); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create from Template</DialogTitle>
            <DialogDescription>
              Select a customer to create a new {useTarget?.type === "quote" ? "quote" : "invoice"} pre-filled with "{useTarget?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <label className="text-sm font-medium block mb-2">Select Customer</label>
            <Popover open={customerPickerOpen} onOpenChange={setCustomerPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between">
                  <span className={!selectedCustomerName ? "text-muted-foreground" : ""}>
                    {selectedCustomerName || "Choose a customer…"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search customers…"
                    value={customerSearch}
                    onValueChange={setCustomerSearch}
                  />
                  <CommandList>
                    {(() => {
                      const trimmed = customerSearch.trim();
                      const hasExactMatch = (customers || []).some(c => c.name.toLowerCase() === trimmed.toLowerCase());
                      const showCreate = trimmed.length > 0 && !hasExactMatch;
                      return (
                        <>
                          {filteredCustomers.length === 0 && !showCreate && (
                            <CommandEmpty>No customers found.</CommandEmpty>
                          )}
                          {filteredCustomers.length > 0 && (
                            <CommandGroup>
                              {filteredCustomers.map(c => (
                                <CommandItem
                                  key={c.id}
                                  value={c.name}
                                  onSelect={() => {
                                    setSelectedCustomerId(c.id);
                                    setSelectedCustomerName(c.name);
                                    setCustomerPickerOpen(false);
                                    setCustomerSearch("");
                                  }}
                                >
                                  {c.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                          {showCreate && (
                            <CommandGroup>
                              <CommandItem
                                value={`__create__${trimmed}`}
                                onSelect={() => {
                                  setPendingCustomerName(trimmed);
                                  setCustomerPickerOpen(false);
                                  setCreateModalOpen(true);
                                }}
                              >
                                <PlusCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                                Create &quot;{trimmed}&quot;
                              </CommandItem>
                            </CommandGroup>
                          )}
                        </>
                      );
                    })()}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isCreating} onClick={() => setUseOpen(false)}>Cancel</Button>
            <Button disabled={!selectedCustomerId || isCreating} onClick={handleUse}>
              {isCreating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
              ) : (
                <>Create {useTarget?.type === "quote" ? "Quote" : "Invoice"}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateCustomerModal
        open={createModalOpen}
        initialName={pendingCustomerName}
        isSubmitting={createCustomer.isPending}
        onOpenChange={setCreateModalOpen}
        onSubmit={handleCreateCustomer}
      />
      </>)}
    </div>
  );
}
